import type { ReactElement } from 'react';
import { useRef } from 'react';
import { Container, Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isArrayOfLength } from '@noah-labs/shared-util-vanilla';
import { motion } from 'framer-motion';
import {
  ArrowRightLeft,
  Globe,
  Landmark,
  LayoutDashboard,
  LockKeyhole,
  ScrollText,
} from 'lucide-react';
import type { MarkdownRemarkFrontmatterCards, Maybe } from '../../../generated/types';
import { CustomCarousel } from '../../components/carousel/CustomCarousel';
import { useBreakpoints } from '../../components/hooks/useBreakpoints';
import { useScrollAnimation } from '../../components/hooks/useScrollAnimation';
import { TextWithNoahLogo } from '../../components/typography/TextWithNoahLogo';
import { HomepageCard } from './HomepageCard';

export const icons = [Globe, ArrowRightLeft, ScrollText, Landmark, LockKeyhole, LayoutDashboard];

type PpCardsSection = {
  cards: MarkdownRemarkFrontmatterCards[];
  textWithIconLabel: Maybe<string> | undefined;
  title: Maybe<string> | undefined;
};

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(12),
  maxWidth: theme.breakpoints.values.lg,
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 3, 6),
  },
})) as typeof Container;

export function CardsSection({ cards, textWithIconLabel, title }: PpCardsSection): ReactElement {
  const { isSmall } = useBreakpoints();
  const containerRef = useRef(null);
  const { opacity, transform: transformUp } = useScrollAnimation(containerRef, {
    opacity: [0, 1],
    transform: ['translateY(50px)', 'translateY(0px)'],
  });
  const { transform: transformDown } = useScrollAnimation(containerRef, {
    opacity: [0, 1],
    transform: ['translateY(0px)', 'translateY(50px)'],
  });

  const groupedCards = cards.reduce((result: MarkdownRemarkFrontmatterCards[][], card, index) => {
    if (index % 2 === 0) {
      result.push([card]);
    } else {
      result[result.length - 1].push(card);
    }
    return result;
  }, []);

  const hasFiveCards = isArrayOfLength(cards, 5);

  return (
    <StyledContainer disableGutters fixed component="section">
      <Stack spacing={3}>
        <div>
          <TextWithNoahLogo>{textWithIconLabel}</TextWithNoahLogo>
          <Typography variant="headingL">{title}</Typography>
        </div>
        {isSmall ? (
          <CustomCarousel
            autoPlay
            infiniteLoop
            preventMovementUntilSwipeScrollTolerance
            showIndicators
            swipeable
            showArrows={false}
            showStatus={false}
            showThumbs={false}
            width="100%"
          >
            {groupedCards.map((cardPair, slideIndex) => (
              <Stack
                key={cardPair[0].id}
                alignItems="center"
                paddingBottom={8}
                spacing={2}
                width="100%"
              >
                <HomepageCard Icon={icons[slideIndex]} {...cardPair[0]} />
                <HomepageCard Icon={icons[slideIndex + 1]} {...cardPair[1]} />
              </Stack>
            ))}
          </CustomCarousel>
        ) : (
          <Grid
            ref={containerRef}
            container
            spacing={2}
            sx={{ justifyContent: 'center', position: 'relative', zIndex: 2 }}
          >
            {cards.map((card, index) => {
              const pushDown = hasFiveCards
                ? index === 4 || index === 5
                : index === 1 || index === 4;
              return (
                <Grid key={card.id} size={4}>
                  <motion.div
                    style={{
                      opacity,
                      transform: pushDown ? transformDown : transformUp,
                    }}
                  >
                    <HomepageCard Icon={icons[index]} {...card} />
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Stack>
    </StyledContainer>
  );
}
